import React, { forwardRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Dialog from '../components/common/Dialog';
import ModalActions from '../components/common/ModalActions';

// 이벤트 트래킹 (회원가입 퍼널 추적)
import {
  trackLoginModalOpen,
  trackLoginModalClose,
  trackLoginModalSignupClick
} from '../utils/eventTracker';
import './LoginPrompt.css';
import { TEACHER_COUNT_LABEL } from '../constants/siteStats';

// 쿠폰 코드별 혜택 정보 (대문자 기준)
const COUPON_INFO = {
  'SPRING14': { days: 14, name: '14일 PRO 무료 체험' },
  'TEACHER30': { days: 14, name: '선생님 2주 무료 체험' },
  'TEACHER7': { days: 7, name: '선생님 7일 무료 체험' },
  'PRO30': { days: 30, name: 'Pro 30일 무료 체험' },
  'FREE30': { days: 30, name: '30일 무료 체험' },
  'AI30': { days: 30, name: '최신 유료 AI 30일 이용권' },
};

// 기능별 맞춤 콘텐츠 — 혜택을 먼저, 차분한 해요체로 (Value First)
export const getFeatureContent = (feature) => {
  const contents = {
    chat: {
      title: '학생 30명 질문도 1분이면 답할 수 있어요',
      subtitle: '교안부터 학부모 안내문까지 AI가 도와드려요',
      benefits: [
        '수업 준비 시간을 80% 줄여요',
        '학부모 안내문을 바로 써요',
        '교안과 학습지를 자동으로 만들어요'
      ]
    },
    saenggibu: {
      title: '연말 생기부, 밤새우지 않아도 돼요',
      subtitle: '학생 30명 생기부를 5분이면 완성해요',
      benefits: [
        '학생별 맞춤 문장을 자동으로 만들어요',
        '2026 기재요령을 반영해요',
        '나이스에 바로 붙여넣을 수 있어요'
      ]
    },
    'saenggibu-continue': {
      title: '한 명을 완성했어요. 나머지도 이어서 만들어보세요',
      subtitle: '무료로 가입하면 제한 없이 만들 수 있어요',
      benefits: [
        '만든 기록을 자동으로 저장해요',
        '학생 수 제한 없이 만들 수 있어요',
        '교과·창체·행발 항목을 모두 지원해요'
      ]
    },
    'saenggibu-similar': {
      title: '이 문장을 30개로 바꿔드릴 수 있어요',
      subtitle: '가입하면 만든 문장이 기록에 저장돼요',
      benefits: [
        '10개까지는 무료로 바로 쓸 수 있어요',
        '만든 기록을 자동으로 저장해요',
        '30개부터는 PRO에서 학급 분량으로 만들어요'
      ]
    },
    inspector: {
      title: '기재요령 위반을 제출 전에 확인해요',
      subtitle: '제출 전 1분 검사로 마음이 편해져요',
      benefits: [
        '위반 항목을 바로 찾고 고쳐요',
        '금지어와 맞춤법을 함께 검사해요',
        '30명을 한 번에 검사해요'
      ]
    },
    lab: {
      title: '서술형 채점, AI에게 맡겨보세요',
      subtitle: 'AI가 채점하고 피드백까지 써드려요',
      benefits: [
        '서술형을 자동으로 채점해요',
        '학생별 피드백을 만들어요',
        '채점 기준표를 원하는 대로 설정해요'
      ]
    },
    scoring: {
      title: '주말까지 채점하지 않아도 돼요',
      subtitle: '100명 서술형도 10분이면 끝나요',
      benefits: [
        '채점 기준대로 AI가 채점해요',
        '학생별 피드백을 만들어요',
        '결과를 엑셀로 저장해요'
      ]
    },
    'assessment-plan': {
      title: '연초 평가계획서, 10분이면 끝나요',
      subtitle: '작년 계획서를 올리면 올해 버전을 만들어드려요',
      benefits: [
        '작년 양식을 그대로 유지해요',
        '배점·시기·방법을 자유롭게 고칠 수 있어요',
        '여러 번 고쳐도 간편해요'
      ]
    },
    ppt: {
      title: '방금 만든 PPT, 계속 만들 수 있어요',
      subtitle: '로그인하면 제한 없이 만들 수 있어요',
      benefits: [
        'PPT를 제한 없이 만들고 내려받아요',
        '이미지가 들어간 PPT를 만들어요',
        '10가지 테마와 스타일을 고를 수 있어요'
      ]
    },
    worksheet: {
      title: '학습지를 계속 만들 수 있어요',
      subtitle: '로그인하면 제한 없이 만들 수 있어요',
      benefits: [
        '학습지를 제한 없이 만들고 인쇄해요',
        '4가지 디자인을 고를 수 있어요',
        'OX·단답형·서술형을 자동으로 출제해요'
      ]
    },
    image: {
      title: '수업 이미지를 바로 만들어요',
      subtitle: '수업 자료에 딱 맞는 이미지를 AI가 만들어드려요',
      benefits: [
        '교육용 다이어그램과 삽화를 만들어요',
        '학습지와 PPT에 쓸 이미지를 만들어요',
        '색칠 도안과 포스터도 만들 수 있어요'
      ]
    },
    // 라이브러리 화면이 넘기는 값. 가입 퍼널 이벤트 이름도 이 값이라 그대로 둔다
    '라이브러리': {
      title: '만든 자료와 담아 둔 자료가 한곳에 모여요',
      subtitle: '무료로 가입하면 바로 쓸 수 있어요',
      benefits: [
        'AI로 만든 PPT·학습지가 자동으로 저장돼요',
        '마음에 드는 나눔터 자료를 즐겨찾기로 모아요',
        '다른 선생님 자료를 내 것으로 가져와 고쳐 써요'
      ]
    },
    default: {
      title: '오늘은 조금 일찍 퇴근해보세요',
      subtitle: '생기부·채점·수업 준비를 AI가 도와드려요',
      benefits: [
        '학생 30명 생기부를 5분에 완성해요',
        '수업 준비 시간을 80% 줄여요',
        `${TEACHER_COUNT_LABEL} 넘는 선생님이 쓰고 있어요`
      ]
    }
  };

  return contents[feature] || contents.default;
};

// 로그인 아이콘 (토스 스타일 아이콘 컨테이너)
const LoginIcon = () => (
  <svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect width="56" height="56" rx="16" fill="#E8F3FF"/>
    <path d="M28 18C24.134 18 21 21.134 21 25C21 28.866 24.134 32 28 32C31.866 32 35 28.866 35 25C35 21.134 31.866 18 28 18Z" fill="#3182F6"/>
    <path d="M28 34C22.477 34 18 36.239 18 39V40H38V39C38 36.239 33.523 34 28 34Z" fill="#3182F6"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path d="M13.333 4L6 11.333 2.667 8" stroke="#3182F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/**
 * 로그인 유도 모달 (Dialog 프리미티브 위)
 * - 혜택 먼저, CTA 하나 ("무료로 시작하기" → /auth, AuthPage의 primary인 Google 시작과 이어진다)
 * - 데스크톱 중앙 다이얼로그 / 모바일 바텀시트, ESC·스크림·X로 닫힌다
 * - props API 유지: { isOpen, onClose, feature }, ref는 패널 요소로 전달된다
 */
const LoginPrompt = forwardRef(({
  isOpen,
  onClose,
  feature = 'default'
}, ref) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCoupon, setPendingCoupon] = useState(null);

  // localStorage에서 대기 중인 쿠폰 코드 확인
  useEffect(() => {
    if (isOpen) {
      const savedCoupon = localStorage.getItem('pending_coupon_code');
      if (savedCoupon && COUPON_INFO[savedCoupon.toUpperCase()]) {
        setPendingCoupon(savedCoupon.toUpperCase());
      }

      // 이벤트 추적
      trackLoginModalOpen(feature, location.pathname);
    }
  }, [isOpen, feature, location.pathname]);

  const couponInfo = pendingCoupon ? COUPON_INFO[pendingCoupon] : null;
  const content = getFeatureContent(feature);

  // 로그인 페이지로 이동
  const handleLogin = () => {
    trackLoginModalSignupClick(feature, location.pathname, couponInfo ? 'with_coupon' : 'no_coupon');
    onClose();
    const currentPath = location.pathname + location.search;
    const redirectParam = encodeURIComponent(currentPath);
    navigate(`/auth?tab=login&redirect=${redirectParam}`);
  };

  // 모달 닫기 (X·스크림·ESC 모두 이 경로로 들어온다)
  const handleClose = (action = 'close_button') => {
    trackLoginModalClose(feature, location.pathname, action);
    onClose();
  };

  const title = couponInfo ? '로그인하면 쿠폰을 바로 받을 수 있어요' : content.title;
  const subtitle = couponInfo
    ? `로그인하면 ${couponInfo.days}일 무료 체험이 자동으로 등록돼요`
    : content.subtitle;
  const benefits = couponInfo
    ? [`${couponInfo.days}일 동안 PRO 기능을 무료로 써요`, '최신 AI 모델을 쓸 수 있어요', '자동 결제는 없어요']
    : content.benefits;

  return (
    <Dialog
      align="center"
      description={subtitle}
      footer={(
        <>
          <ModalActions
            primary={{
              label: couponInfo ? '쿠폰 받고 로그인하기' : '무료로 시작하기',
              onClick: handleLogin,
            }}
            testId="login-modal-actions"
          />
          <p className="login-modal__footnote">Google 계정으로 10초 만에 시작할 수 있어요</p>
        </>
      )}
      icon={<LoginIcon />}
      onClose={() => handleClose('dismiss')}
      open={Boolean(isOpen)}
      panelClassName="login-modal"
      panelRef={ref}
      size="sm"
      testId="login-modal"
      title={title}
    >
      {couponInfo && (
        <p className="login-modal__coupon">
          {couponInfo.days}일 무료 쿠폰이 준비돼 있어요
        </p>
      )}
      <ul className="login-modal__benefits">
        {benefits.map((benefit) => (
          <li className="login-modal__benefit" key={benefit}>
            <CheckIcon />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>
    </Dialog>
  );
});

export default LoginPrompt;
